import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon, PageHeader } from '../../ui/index.js';
import { clearRecoveryCode, readRecoveryCode, type RecoveryCodeReason } from './recoveryStorage.js';

const DESCRIPTIONS: Record<RecoveryCodeReason, string> = {
  signup:
    "Questo codice è l'unico modo per recuperare le tue foto se dimentichi la passphrase. Lo generiamo una sola volta e non lo memorizziamo da nessuna parte.",
  rotated:
    "Ecco il tuo nuovo codice di recupero. Il precedente non funziona più: aggiornalo ovunque l'avessi salvato.",
  recovered:
    'Hai ripristinato il vault e impostato una nuova passphrase. Il codice che hai appena usato è stato consumato: questo è il suo sostituto, salvalo subito.',
};

export function RecoveryCodePage() {
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const stashed = useMemo(() => readRecoveryCode(), []);

  useEffect(() => {
    if (!stashed) navigate('/login', { replace: true });
  }, [stashed, navigate]);

  if (!stashed) return null;

  const code = stashed.code;
  const groups = code.split('-');

  async function handleCopy() {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function handleContinue() {
    clearRecoveryCode();
    navigate('/', { replace: true });
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-7 py-2">
      <PageHeader
        eyebrow="codice di recupero"
        title={
          <>
            Stampa, salva, <span className="accent-text">non perderlo</span>
          </>
        }
        description={DESCRIPTIONS[stashed.reason]}
      />

      <Card className="flex flex-col gap-6">
        <div className="grid grid-cols-3 gap-3 font-display text-lg tracking-[0.18em] sm:grid-cols-4">
          {groups.map((group, idx) => (
            <div
              key={`${group}-${idx}`}
              className="rounded-lg border border-outline bg-surface-container px-3 py-3 text-center text-cyan"
            >
              {group}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            onClick={handleCopy}
            leftIcon={<Icon name={copied ? 'check' : 'content_copy'} className="text-base" />}
          >
            {copied ? 'Copiato' : 'Copia codice'}
          </Button>
          <label className="ml-auto inline-flex cursor-pointer items-center gap-3 text-sm text-on-surface-variant">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-4 w-4 rounded border-outline bg-surface-container-low accent-cyan"
            />
            Ho salvato il codice in luogo sicuro
          </label>
        </div>

        <Button
          size="lg"
          disabled={!acknowledged}
          onClick={handleContinue}
          rightIcon={<Icon name="arrow_forward" className="text-base" />}
        >
          Entra nel vault
        </Button>
      </Card>
    </div>
  );
}
